import { ErrorNotice } from "../ui/ErrorNotice.js";
/**
 * Legacy upload-destination credentials — a CLEANUP affordance, not a feature.
 *
 * The "Upload destinations" CRUD UI was removed (see docs/audit-history.md, round 4): no
 * exporter ever read a connector, and the SFTP+private-key auth its own form defaulted to has
 * no client transport at all (`apps/desktop/src-tauri/src/ftp.rs`: "ftps/sftp not yet
 * supported"). It stored encrypted per-tenant credentials that could never ship a byte.
 *
 * Removing the screen left those credentials in the database with no way to see or delete
 * them. That is the actual problem this file solves — and ONLY that. It lists whatever rows
 * still exist and lets an admin delete them. It deliberately offers no create, no edit and no
 * test: re-adding the feature means re-adding `ConnectorsSection` from git alongside a
 * transport that can actually upload, not growing this block.
 *
 * Renders NOTHING when there are no rows, so a workspace that never used connectors never
 * sees it — and once cleaned out, it disappears for good.
 */
import { confirmAction } from "../ui/confirmAction.js";
import { useCallback, useEffect, useState } from "react";
import { useServices } from "../app/context.js";
import type { StorageConnector } from "@genz/platform";
import { enumLabel } from "../format/labels.js";
import { InfoHint } from "../ui/Tooltip.js";
import { RowAction } from "../ui/RowAction.js";

export function LegacyConnectors({ active = true, onRowCount }: {
  /** WS-Z4: false while another Settings section is open. The block still MOUNTS and still
   *  loads, because `onRowCount` is what tells the sub-nav whether this entry exists at all;
   *  it simply draws nothing until it is the open section. */
  active?: boolean;
  /** How many leftover credential rows this workspace has. */
  onRowCount?: (n: number) => void;
} = {}) {
  const { client } = useServices();
  const [rows, setRows] = useState<StorageConnector[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await client.listConnectors());
    } catch {
      // A workspace with no connector access (or an older cloud) simply has nothing to clean.
      setRows([]);
    }
  }, [client]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { onRowCount?.(rows?.length ?? 0); }, [onRowCount, rows]);

  async function remove(c: StorageConnector) {
    // (The `no-alert` disable that sat here was dead: the rule is not configured in either
    //  eslint config, so the directive only tripped the strict overlay's unused-directive
    //  check once WS-Z4 brought this file into the changed set. The `window.confirm` guard
    //  below is unchanged.)
    if (!await confirmAction(
        `Delete the stored credentials for “${c.label}”? Nothing uses them — no export path ` +
        `reads a connector — so this only removes the saved secret. This cannot be undone.`,
      )) return;
    setBusy(c.id);
    setError(null);
    try {
      await client.deleteConnector(c.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  if (!active || !rows || rows.length === 0) return null;

  return (
    <section data-testid="legacy-connectors">
      {/* WS-Z4 / rule 10: the explanation was a hint PARAGRAPH seven lines deep under the
          heading. It is the same sentence, in the one explanation channel, on the heading it
          explains. The wrapper `<span>` carries the testid because `InfoHint` takes none and
          `ui/Tooltip.tsx` belongs to another wave. */}
      <h3>
        Legacy upload destinations <span className="badge badge--warn">unused</span>{" "}
        <span data-testid="legacy-connectors-note">
          <InfoHint
            label="Why these credentials are here"
            text="These stored credentials are left over from the old Upload-destinations screen, which was removed because no export path ever read them — the desktop transport does not speak SFTP, which is what they were mostly configured for. They are doing nothing except sitting in the database encrypted. Delete them. Server exports are configured under FTP / SFTP export above."
          />
        </span>
      </h3>
      {error && <p role="alert" data-testid="legacy-connectors-error"><ErrorNotice detail={error} /></p>}
      <ul className="settings__preset-list">
        {rows.map((c) => (
          <li key={c.id} data-testid={`legacy-connector-${c.id}`}>
            <span className="connectors__name">{c.label}</span>
            {/* The provider is an enum on the wire ("sftp"); the screen rule knows the
                acronym table, so it reads "SFTP" rather than a machine string (m-3). */}
            <span className="faint">{enumLabel(c.provider)}</span>
            <RowAction
              label={busy === c.id ? "Deleting…" : "Delete credentials"}
              icon="trash"
              tone="danger"
              disabled={busy === c.id}
              busy={busy === c.id}
              data-testid={`legacy-connector-delete-${c.id}`}
              onClick={() => void remove(c)}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}
